'use client'

import { useFeatureFlag } from '@dustin-web/microsite-data/src/feature-flags/client'
import { NumericProps, useTranslator } from '@eo-locale/react'
import { Translator } from '@eo-locale/core'
import { FormatMessageOptions } from '@eo-locale/core'
import { useSiteContext } from '../hooks/use-site-context'
import { useUserSettings } from '../hooks/use-user-settings'
import { getDefaultLanguage } from '@dustin-web/microsite-config'
import { getCultureCode } from '@dustin-web/microsite-config'
import { useCallback, useMemo } from 'react'
import { parseMarkdown } from './markdown'

export const useI18n = () => {
  const { site, currencyCode: currency } = useSiteContext()
  const { showPricesInclVat } = useUserSettings()
  const defaultSiteLanguageTranslator = useMemo(
    () => new Translator(getCultureCode(getDefaultLanguage(site))),
    [site]
  )
  const translator = useTranslator() // Seeded with user language messages in Translation provider
  const { enabled: isShowExVatTextFor0VatEnabled } = useFeatureFlag('show-ex-vat-text-for-0-vat')

  const getIncExVatIncExVatText = useCallback(
    (showPricesIncVat: boolean) => {
      try {
        return translator.translate(showPricesIncVat ? 'includingvatshort' : 'excludingvatshort')
      } catch (error) {
        return showPricesIncVat ? 'includingvatshort' : 'excludingvatshort'
      }
    },
    [translator]
  )

  return {
    formatCurrency: useCallback(
      (amount: number, opts = {} as NumericProps) => {
        const isInteger = Number.isInteger(amount)

        const currencyDisplay = 'symbol'

        const formattedNumber = defaultSiteLanguageTranslator.formatNumber(amount, {
          currency,
          currencyDisplay,
          maximumFractionDigits: isInteger ? 0 : undefined,
          minimumFractionDigits: isInteger ? 0 : undefined,
          style: 'currency',
          ...opts,
        })

        return formattedNumber
      },
      [currency, defaultSiteLanguageTranslator]
    ),
    incExVatTranslation: useCallback(
      (vat: number | undefined | null) => {
        if (vat === 0 && isShowExVatTextFor0VatEnabled) {
          return getIncExVatIncExVatText(false)
        }

        return getIncExVatIncExVatText(showPricesInclVat)
      },
      [getIncExVatIncExVatText, isShowExVatTextFor0VatEnabled, showPricesInclVat]
    ),

    // formatNumber: (amount: number, opts: FormatNumberOptions) => formatNumber(amount, opts),
    translate: useCallback(
      (key: string, options: FormatMessageOptions = {}) => {
        try {
          return translator.translate(key.toLowerCase(), options)
        } catch (error) {
          return key.toLowerCase()
        }
      },
      [translator]
    ),
    translateMarkdown: useCallback(
      (key: string, options: FormatMessageOptions = {}) => {
        try {
          return parseMarkdown(translator.translate(key.toLowerCase(), options))
        } catch (error) {
          return key.toLowerCase()
        }
      },
      [translator]
    ),
    translationsExist: useCallback(
      (key: string) => {
        try {
          return key.toLowerCase() !== translator.getMessageById(key.toLowerCase())
        } catch (error) {
          return false
        }
      },
      [translator]
    ),
    translationsExistNonEmpty: useCallback(
      (key: string) => {
        try {
          return (
            key.toLowerCase() !== translator.getMessageById(key.toLowerCase()) &&
            translator.translate(key.toLowerCase()) !== ''
          )
        } catch (error) {
          return false
        }
      },
      [translator]
    ),
  }
}
